import { useState } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import { CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TabsContent } from "@/components/ui/tabs";
import api, { errMsg, fmtDate } from "@/lib/api";

export function CalendarTab({ holidays, shifts, employees, onRefresh }) {
  const [holiday, setHoliday] = useState({ name: "", date: "", type: "company" });
  const [shift, setShift] = useState({ name: "", start_time: "09:00", end_time: "18:00" });

  const addHoliday = async () => {
    try {
      await api.post("/employer/holidays", holiday);
      toast.success("Holiday added");
      setHoliday({ name: "", date: "", type: "company" });
      onRefresh();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const addShift = async () => {
    try {
      await api.post("/employer/shifts", shift);
      toast.success("Shift created");
      setShift({ name: "", start_time: "09:00", end_time: "18:00" });
      onRefresh();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const assignShift = async (employeeId, shiftId) => {
    try {
      await api.post(`/employer/employees/${employeeId}/shift`, { shift_id: shiftId || null });
      toast.success("Shift updated");
      onRefresh();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <TabsContent value="calendar">
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-border/60 rounded-xl p-6" data-testid="holidays-panel">
          <h3 className="font-heading font-semibold text-[#0B1B3D] mb-4">Holiday calendar</h3>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <Input placeholder="Holiday name" value={holiday.name} onChange={(e) => setHoliday({ ...holiday, name: e.target.value })} data-testid="holiday-name" />
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="justify-start font-normal" data-testid="holiday-date">
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  {holiday.date ? format(new Date(holiday.date + "T00:00:00"), "dd MMM yyyy") : "Pick date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={holiday.date ? new Date(holiday.date + "T00:00:00") : undefined}
                  onSelect={(d) => d && setHoliday({ ...holiday, date: format(d, "yyyy-MM-dd") })}
                />
              </PopoverContent>
            </Popover>
            <Select value={holiday.type} onValueChange={(v) => setHoliday({ ...holiday, type: v })}>
              <SelectTrigger data-testid="holiday-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="national">National</SelectItem>
                <SelectItem value="state">State</SelectItem>
                <SelectItem value="company">Company</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button size="sm" onClick={addHoliday} disabled={!holiday.name || !holiday.date} className="rounded-full bg-[#0B1B3D]" data-testid="add-holiday-btn">Add Holiday</Button>
          <div className="space-y-2 mt-5">
            {holidays.map((h) => (
              <div key={h.id} className="flex items-center justify-between border border-border/60 rounded-lg px-4 py-2.5" data-testid={`holiday-${h.id}`}>
                <div>
                  <p className="text-sm font-medium">{h.name}</p>
                  <p className="text-xs text-muted-foreground">{fmtDate(h.date)} · <span className="capitalize">{h.type}</span></p>
                </div>
                <Button size="sm" variant="ghost" className="text-red-600" onClick={async () => { await api.delete(`/employer/holidays/${h.id}`); onRefresh(); }} data-testid={`holiday-del-${h.id}`}>Remove</Button>
              </div>
            ))}
            {holidays.length === 0 && <p className="text-sm text-muted-foreground">No holidays configured.</p>}
          </div>
        </div>

        <div className="bg-white border border-border/60 rounded-xl p-6" data-testid="shifts-panel">
          <h3 className="font-heading font-semibold text-[#0B1B3D] mb-4">Shifts & assignment</h3>
          <div className="grid grid-cols-4 gap-3 mb-4">
            <Input placeholder="Shift name" value={shift.name} onChange={(e) => setShift({ ...shift, name: e.target.value })} className="col-span-2" data-testid="shift-name" />
            <Input type="time" value={shift.start_time} onChange={(e) => setShift({ ...shift, start_time: e.target.value })} data-testid="shift-start" />
            <Input type="time" value={shift.end_time} onChange={(e) => setShift({ ...shift, end_time: e.target.value })} data-testid="shift-end" />
          </div>
          <Button size="sm" onClick={addShift} disabled={!shift.name} className="rounded-full bg-[#0B1B3D]" data-testid="add-shift-btn">Create Shift</Button>
          <div className="space-y-2 mt-5">
            {shifts.map((s) => (
              <div key={s.id} className="flex items-center justify-between border border-border/60 rounded-lg px-4 py-2.5" data-testid={`shift-${s.id}`}>
                <p className="text-sm font-medium">{s.name} <span className="text-xs text-muted-foreground">{s.start_time}–{s.end_time} · {s.days.join(", ")}</span></p>
                <Button size="sm" variant="ghost" className="text-red-600" onClick={async () => { await api.delete(`/employer/shifts/${s.id}`); onRefresh(); }} data-testid={`shift-del-${s.id}`}>Remove</Button>
              </div>
            ))}
          </div>
          <h4 className="font-heading font-semibold text-sm text-[#0B1B3D] mt-6 mb-3">Assign to employees</h4>
          <div className="space-y-2">
            {employees.map((e) => (
              <div key={e.id} className="flex items-center justify-between gap-3 border border-border/50 rounded-lg px-4 py-2.5" data-testid={`shift-emp-${e.id}`}>
                <p className="text-sm font-medium">{e.name}</p>
                <Select value={e.shift_id || "none"} onValueChange={(v) => assignShift(e.id, v === "none" ? null : v)}>
                  <SelectTrigger className="w-44 h-8 text-xs" data-testid={`shift-select-${e.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No shift</SelectItem>
                    {shifts.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
            {employees.length === 0 && <p className="text-sm text-muted-foreground">No active employees.</p>}
          </div>
        </div>
      </div>
    </TabsContent>
  );
}
